// Wires the badge scanner FSM (src/scanner.ts) to the app. Initialised ONCE from App.vue, so the
// burst listener is live on every screen (DESIGN.md section 9.4) — the FSM itself already stands
// down while a text input has real focus; nothing here re-scopes it per screen.
//
// Screen semantics: "A badge scan on Home starts a new match with that player on the LEFT side"
// (section 9.1); on New match a scan fills whichever side is currently active — both preserved
// exactly as before, and the screen transition / filled side IS the feedback, so neither also
// toasts. Every OTHER screen has no contextual meaning for a scan, so it surfaces a global toast
// (components/ToastStack.vue via store/toast.ts) instead of silently dropping the resolved
// identity — that silent drop was the 2026-08-31 live bug: a user scanned on a non-home/newMatch
// screen, the burst resolved fine, and nothing was ever shown.
//
// Every resolution (success or failure, on any screen) also plays the same scan beep app-vue2's
// scanner UIs use (ts/composables/useBeep.ts) — see src/sounds.ts scanSuccess/scanError.
import { pushToast } from '../store/toast'
import { sounds } from '../sounds'
import { initScanner } from '../scanner'
import { nav, navigate } from '../store/nav'
import { draft, fillActiveSide, resetDraft, setSide } from '../store/draft'
import { registerScannedPlayer } from '../store/tournament'
import { markNextUpScanned } from '../store/nextUp'
import type { IdentityResolveResponse } from '../types'

export function useGlobalScanner() {
  function handleResolved(result: IdentityResolveResponse) {
    const { player } = result

    // Round-13 "Next up…": while a tournament result overlay is waiting for the next pairing to
    // check in, a scan from either of those two players marks them ready (LiveMatch auto-starts
    // the slot once both are). Takes precedence over every screen branch below.
    if (markNextUpScanned(player)) {
      sounds.scanSuccess()
      pushToast('success', `${player.displayName} is ready`, player)
      return
    }
    // Round-11: the success beep no longer fires unconditionally up front — a resolved badge
    // that the current screen then REJECTS (e.g. scanning the player already on the other side
    // of the New match draft) must sound like an error, not a success. Each accepting branch
    // beeps success itself; rejecting branches beep error.
    if (nav.screen === 'home') {
      sounds.scanSuccess()
      resetDraft()
      setSide('left', player)
      navigate('newMatch')
      return
    }
    if (nav.screen === 'newMatch') {
      if ((draft.activeSide === 'left' && draft.right?.id === player.id) || (draft.activeSide === 'right' && draft.left?.id === player.id)) {
        sounds.scanError()
        pushToast('error', `${player.displayName} is already on the other side`)
        return
      }
      sounds.scanSuccess()
      fillActiveSide(player)
      return
    }
    if (nav.screen === 'tournaments' || nav.screen === 'tournamentDetail') {
      sounds.scanSuccess()
      // Round-8 feedback item 21 — "Scan to register". Round-11 (multiple open tournaments):
      // on a tournament's DETAIL screen the scan registers into THAT tournament
      // (nav.params.tournamentId); the list screen falls back to the newest open one
      // (registerScannedPlayer's store-based pick). Both push their own toasts and return
      // false when there's nothing to register into — that case falls through to the
      // generic scanned toast.
      void registerScannedPlayer(player, nav.screen === 'tournamentDetail' ? nav.params.tournamentId : undefined).then((handled) => {
        if (!handled) pushToast('success', `${player.displayName} scanned`, player)
      })
      return
    }
    // Live/PastMatches/PastMatchDetail/Rankings/Versus/Player/Debug/any overlay: a scan has no
    // in-context action here — the toast IS the feedback (task item 2: "otherwise the toast
    // alone is fine for now").
    sounds.scanSuccess()
    pushToast('success', `${player.displayName} scanned`, player)
  }

  function handleError(err: unknown) {
    sounds.scanError()
    const status = (err as { status?: number })?.status
    const code = (err as { code?: string })?.code
    let message = 'Could not read badge'
    if (status === 0 || (typeof status === 'number' && status >= 500)) {
      message = 'Kombo unreachable'
    } else if (code === 'expired_qr') {
      // Round-9 (2026-08-31): an unknown idshare_ token is almost always a stale Account-page
      // QR (5-min token, also flushed by Kombo deploys) — say so instead of "not recognised".
      message = 'QR expired — reopen your Kombo Account page and scan again'
    } else if (status === 404) {
      message = 'Badge not recognised'
    } else if (status === 400) {
      message = "Couldn't read badge — open your Kombo app profile for a fresh QR"
    }
    pushToast('error', message)
  }

  const handle = initScanner(handleResolved, handleError)
  return () => handle.stop()
}
